import { useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  FormHelperText,
  FormLabel,
  IconPlusCircle,
  Input,
  Tooltip,
  Typography,
  useDropdownState,
  Wrapper
} from '@screentone/core';

import { BlockDividerButton } from 'components/block-divider-button/BlockDividerButton';
import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { screamingSnakeCaseToTitleCase, toScreamingSnakeCase } from 'utils/case';
import { makeDropdownInputKeyHandlers } from 'utils/keyboard';
import { WHITESPACE } from 'utils/regex';
import styles from './AddGroupButton.module.scss';

interface AddGroupButtonProps {
  insertAfterGroupId?: string | null;
}

export const AddGroupButton = ({ insertAfterGroupId }: AddGroupButtonProps) => {
  const { open, setOpen, componentRef } = useDropdownState();
  const { layout, addGroup } = useStructureEditContext();
  const [raw, setRaw] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const id = toScreamingSnakeCase(raw);
  const label = id ? screamingSnakeCaseToTitleCase(id) : '';
  const existingIds = (layout.structure?.groups ?? []).map((g) => g.id);
  const isDuplicate = existingIds.includes(id);
  const isValid = id.length > 0 && !isDuplicate;

  const handleToggle = () => {
    setOpen(!open);
    if (!open) {
      setRaw('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleAdd = () => {
    if (!isValid) return;
    addGroup(label, insertAfterGroupId);
    setRaw('');
    setOpen(false);
  };

  const handleKeyDown = makeDropdownInputKeyHandlers({
    onSubmit: handleAdd,
    onDismiss: () => {
      setOpen(false);
      setRaw('');
    }
  });

  return (
    <BlockDividerButton open={open}>
      <Dropdown
        arrow="top"
        position="center"
        margin={{ horizontal: 'xs' }}
        componentRef={componentRef}
        open={open}
        onToggle={handleToggle}
      >
        <Dropdown.Trigger>
          <Tooltip>
            <Tooltip.Content position="right">Add group</Tooltip.Content>
            <Tooltip.Trigger>
              <Button tertiary icon={IconPlusCircle} onClick={handleToggle} />
            </Tooltip.Trigger>
          </Tooltip>
        </Dropdown.Trigger>
        <Dropdown.Content
          className={styles.blockDividerDropdownContent}
          arrow="top"
          margin={{ top: 'sm' }}
          padding={{ all: 'none' }}
        >
          <Wrapper padding={{ all: 'md' }}>
            <FormLabel label="Group ID" labelPosition="top">
              <Input
                ref={inputRef}
                value={raw}
                placeholder="e.g. TOP_STORIES"
                data-testid="add-group-input"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setRaw(e.target.value.replace(WHITESPACE, ''));
                }}
                onKeyDown={handleKeyDown}
              />
            </FormLabel>
            <Typography variant="note" color="asphalt" margin={{ top: 'xs', bottom: 'none' }}>
              Letters, numbers, and underscores only
            </Typography>
            {id && (
              <Typography variant="note" color="asphalt" margin={{ top: 'xs', bottom: 'none' }}>
                Label: <strong>{label}</strong>
              </Typography>
            )}
            {isDuplicate && (
              <FormHelperText error margin={{ top: 'xs' }}>
                A group with this ID already exists.
              </FormHelperText>
            )}
            <Button
              primary
              fullWidth
              margin={{ top: 'xs' }}
              onClick={handleAdd}
              disabled={!isValid}
              data-testid="add-group-submit"
            >
              Add group
            </Button>
          </Wrapper>
        </Dropdown.Content>
      </Dropdown>
    </BlockDividerButton>
  );
};
